import { useState } from "react";
import { FileText } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { Search } from "@/components/ui/Search";
import { Tabs } from "@/components/ui/Tabs";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { LoadingState } from "@/components/feedback/LoadingState";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { ConfirmDialog } from "@/components/modals/ConfirmDialog";
import { Textarea } from "@/components/ui/Textarea";
import { DriverChangesCard } from "./DriverChangesCard";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useApproveKyc,
  useKycApplication,
  useKycList,
  useOpenKycDocument,
  useRejectKyc,
} from "@/features/kyc/useKyc";
import { useSocketEvent } from "@/hooks/useSocketEvent";
import { SocketEvents } from "@/sockets/socket";
import { toast } from "@/store/toast.store";
import { formatDate } from "@/utils/format";
import type { KycStatus } from "@/types/domain";
import { cn } from "@/utils/cn";

const TABS: { value: KycStatus | "all"; label: string }[] = [
  { value: "all", label: "Pending" },
  { value: "VERIFIED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

export default function DriverApprovals() {
  const [tab, setTab] = useState<KycStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const debouncedSearch = useDebounce(search);

  // "all" = the backend's default filter (IN_REVIEW), i.e. the open queue awaiting review.
  const list = useKycList({
    status: tab === "all" ? undefined : tab,
    search: debouncedSearch || undefined,
    limit: 50,
  });

  useSocketEvent<{ type: string }>(SocketEvents.Notification, (payload) => {
    if (payload?.type === "KYC_SUBMITTED") {
      list.refetch();
    }
  });

  const activeId = selectedId ?? list.data?.items[0]?.id;
  const detail = useKycApplication(activeId);
  const approve = useApproveKyc();
  const reject = useRejectKyc();
  const openDocument = useOpenKycDocument();

  return (
    <PageContainer
      title={`KYC queue · ${list.data?.total ?? 0}`}
      description="Verify driver documents and approve or reject their KYC application."
      actions={<Tabs items={TABS} value={tab} onChange={(v) => setTab(v as KycStatus)} />}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        <div className="card flex flex-col">
          <div className="border-b border-border p-3">
            <Search
              value={search}
              onChange={setSearch}
              placeholder="Search driver…"
              className="relative w-full"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {list.isLoading && <LoadingState label="Loading queue…" />}
            {list.error && <ErrorState error={list.error} onRetry={() => list.refetch()} />}
            {list.data?.items.length === 0 && <EmptyState title="Queue is empty" />}
            <ul>
              {list.data?.items.map((app) => (
                <li key={app.id}>
                  <button
                    onClick={() => setSelectedId(app.id)}
                    className={cn(
                      "flex w-full items-center justify-between border-b border-border/60 px-4 py-3 text-left hover:bg-cream-200/60",
                      activeId === app.id && "bg-cream-200",
                    )}
                  >
                    <span className="text-sm font-medium text-ink">
                      {app.driver?.name ?? "Unnamed driver"}
                    </span>
                    <StatusBadge status={app.status} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="card p-5">
          {!activeId && (
            <EmptyState
              title="Select a driver"
              description="Pick an application from the queue to review."
            />
          )}
          {detail.isLoading && activeId && <LoadingState label="Loading application…" />}
          {detail.error && <ErrorState error={detail.error} onRetry={() => detail.refetch()} />}

          {detail.data && (
            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-ink">
                    {detail.data.driver?.name ?? detail.data.fullName ?? "Unnamed driver"}
                  </h3>
                  <p className="text-sm text-ink-faint">
                    {detail.data.driver?.phone}
                    {detail.data.submittedAt ? ` · submitted ${formatDate(detail.data.submittedAt)}` : ""}
                  </p>
                </div>
                <StatusBadge status={detail.data.status} />
              </div>

              {detail.data.vehicleNumber && (
                <div className="text-sm text-ink-muted">Vehicle: {detail.data.vehicleNumber}</div>
              )}

              <div>
                <h4 className="mb-2 text-sm font-semibold text-ink">Documents</h4>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {detail.data.checklist.map((doc) =>
                    doc.type === "BANK_UPI" && !doc.documentId ? (
                      <div
                        key={doc.type}
                        className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-border bg-cream-200/60 p-2 text-ink-muted"
                      >
                        <span className="text-xs text-center">{doc.label}</span>
                        <span className="break-all text-center text-sm font-medium text-ink">
                          {doc.documentNumber || "—"}
                        </span>
                        {doc.status && <StatusBadge status={doc.status} className="mt-1" />}
                      </div>
                    ) : (
                      <button
                        key={doc.type}
                        type="button"
                        disabled={!doc.documentId}
                        onClick={() => doc.documentId && openDocument.mutate(doc.documentId)}
                        className={cn(
                          "flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border text-ink-muted",
                          doc.documentId
                            ? "border-border bg-cream-200/60 hover:border-teal-500"
                            : "border-dashed border-border/60 opacity-50",
                        )}
                      >
                        <FileText className="h-6 w-6" />
                        <span className="text-xs text-center">{doc.label}</span>
                        {doc.status && <StatusBadge status={doc.status} className="mt-1" />}
                      </button>
                    ),
                  )}
                </div>
              </div>

              {(detail.data.status === "PENDING" || detail.data.status === "IN_REVIEW") && (
                <div className="flex justify-end gap-2 border-t border-border pt-4">
                  <Button variant="outline" onClick={() => setRejecting(true)}>
                    Reject
                  </Button>
                  <Button
                    variant="primary"
                    loading={approve.isPending}
                    onClick={() =>
                      approve.mutate(
                        { id: detail.data.id },
                        { onSuccess: () => toast.success("Driver approved") },
                      )
                    }
                  >
                    Approve driver
                  </Button>
                </div>
              )}

              {detail.data.status === "REJECTED" && detail.data.rejectionReason && (
                <p className="text-sm text-danger-500">Reason: {detail.data.rejectionReason}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <DriverChangesCard />

      <ConfirmDialog
        open={rejecting}
        title="Reject this application?"
        description="This reason is shown to the driver."
        confirmLabel="Reject"
        loading={reject.isPending}
        confirmDisabled={rejectReason.trim().length < 3}
        onCancel={() => {
          setRejecting(false);
          setRejectReason("");
        }}
        onConfirm={() => {
          if (!activeId) return;
          reject.mutate(
            { id: activeId, reason: rejectReason.trim() },
            {
              onSuccess: () => {
                toast.success("Application rejected");
                setRejecting(false);
                setRejectReason("");
              },
            },
          );
        }}
      >
        <Textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. RC photo is blurry, please re-upload"
          rows={3}
        />
      </ConfirmDialog>
    </PageContainer>
  );
}
